import {
  Dispatch,
  FC,
  Fragment,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from "react";
import { Transition } from "@headlessui/react";
import { useMutation } from "@apollo/client/react";
import { AiOutlineLoading } from "react-icons/ai";
import { QRCode } from "react-qrcode-logo";
import { toast } from "react-toastify";
import NotificationToast from "../toasts/notification.toast.tsx";
import { NotificationType } from "../../enums/notification.enum.ts";
import { AppDispatch } from "../../../store/index.ts";
import { useDispatch } from "react-redux";
import { setP2faEnabled } from "../../../store/user.store.ts";
import {
  GenerateP2FAResponse,
  OnP2FAResponse,
} from "../../types/gql/response.type.ts";
import { PAYOUT_2FA_GENERATE_MUTATION } from "../../../api/graphql/mutations/payout-2fa-generate.mutation.ts";
import { PAYOUT_2FA_ON_MUTATION } from "../../../api/graphql/mutations/payout-2fa-on.mutation.ts";

type PayoutTFAOnModalProps = {
  isGlobalEnabled: boolean;
  show: boolean;
  setShow: Dispatch<SetStateAction<boolean>>;
};

const PayoutTFAOnModal: FC<PayoutTFAOnModalProps> = ({
  isGlobalEnabled,
  show,
  setShow,
}) => {
  const dispatcher: AppDispatch = useDispatch();
  const [code, setCode] = useState<string>("");
  const [globalCode, setGlobalCode] = useState<string>("");

  const [generateTfa, generateTfaData] = useMutation<GenerateP2FAResponse>(
    PAYOUT_2FA_GENERATE_MUTATION,
  );
  const [onTfa, onTfaData] = useMutation<OnP2FAResponse>(
    PAYOUT_2FA_ON_MUTATION,
  );

  const tfaUri: string = generateTfaData.data?.generatePTFA?.uri || "";
  const tfaSecret: string = generateTfaData.data?.generatePTFA?.secret || "";

  const closeModal = useCallback((): void => {
    setCode("");
    setShow(false);
  }, [setShow]);

  useEffect(() => {
    if (show) {
      generateTfa().then();
    }
  }, [generateTfa, isGlobalEnabled, show]);

  useEffect(() => {
    if (generateTfaData.error) {
      toast(
        <NotificationToast
          type={NotificationType.WARNING}
          title="2FA Code"
          message="Error generating try again later"
        />,
        {
          toastId: "generate-tfa-error",
        },
      );
      // eslint-disable-next-line react-hooks/set-state-in-effect
      closeModal();
    }
  }, [generateTfaData.error, closeModal]);

  useEffect(() => {
    if (onTfaData.data) {
      const { onPTFA } = onTfaData.data;

      if (onPTFA) {
        toast(
          <NotificationToast
            type={NotificationType.SUCCESS}
            title="Approval 2FA"
            message="Successfully enabled"
          />,
          {
            toastId: "on-p2fa-success",
          },
        );

        dispatcher(setP2faEnabled(true));
        // eslint-disable-next-line react-hooks/set-state-in-effect
        closeModal();
      }
    }
  }, [dispatcher, onTfaData, onTfaData.data, closeModal]);

  useEffect(() => {
    if (onTfaData.error) {
      toast(
        <NotificationToast
          type={NotificationType.WARNING}
          title="Approval 2FA Code"
          message={onTfaData.error.message}
        />,
        {
          toastId: "on-p2fa-error",
        },
      );
    }
  }, [onTfaData.error]);

  const validateTFAHandler = useCallback(async () => {
    await onTfa({
      variables: { code, globalCode },
    });
  }, [code, onTfa, globalCode]);

  return (
    <>
      <Transition
        as={Fragment}
        show={show}
        enter="transition ease-out duration-100"
        enterFrom="opacity-0 translate-y-1"
        enterTo="opacity-100 translate-y-0"
        leave="transition ease-in duration-80"
        leaveFrom="opacity-100 translate-y-0"
        leaveTo="opacity-0 translate-y-1"
      >
        <div className="fixed inset-0 bg-slate-900/30 z-50 transition-opacity"></div>
      </Transition>
      <Transition
        as={Fragment}
        show={show}
        enter="transition ease-in-out duration-200"
        enterFrom="opacity-0 translate-y-4"
        enterTo="opacity-100 translate-y-0"
        leave="transition ease-in-out duration-200"
        leaveFrom="opacity-100 translate-y-0"
        leaveTo="opacity-0 translate-y-4"
      >
        <div className="fixed inset-0 z-50 overflow-hidden flex items-center my-4 justify-center transform px-4 sm:px-6">
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-sm shadow-lg overflow-auto max-w-lg w-full max-h-full"
          >
            {/* Modal header */}
            <div className="p-4">
              <div className="text-lg font-semibold text-slate-800">
                Enable Approval 2FA
              </div>
            </div>
            <div className="divide-y">
              <div></div>
              <div></div>
            </div>
            {/* Modal content */}
            <div className="p-4 pb-0">
              <div className="mb-4 flex items-center flex-col justify-center transition-all">
                <div>
                  {generateTfaData.loading || !tfaUri ? (
                    <div className="p-20">
                      <AiOutlineLoading
                        size={32}
                        className="transition-all animate-spin"
                      />
                    </div>
                  ) : (
                    <QRCode
                      ecLevel="L"
                      eyeRadius={[5, 5, 5]}
                      fgColor="#223049"
                      value={tfaUri}
                      enableCORS={true}
                      size={250}
                    />
                  )}
                </div>
                <p className="text-center font-medium text-slate-800">
                  Or Copy and Paste this Code:
                </p>
                <p className="text-center font-bold">{tfaSecret}</p>

                {isGlobalEnabled && (
                  <div className="col-span-6 form-control w-full mt-4">
                    <input
                      id="global-2fa-code"
                      value={globalCode}
                      onChange={(e) => setGlobalCode(e.target.value)}
                      placeholder="Global 2FA Code"
                      className="form-input w-full max-w-161.5 mr-1"
                      type="text"
                      autoFocus
                    />
                  </div>
                )}

                <div className="col-span-6 form-control w-full mt-4">
                  <input
                    id="payout-2fa-code"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    placeholder="Approval 2FA Code"
                    className="form-input w-full max-w-161.5 mr-1"
                    type="text"
                    autoFocus={!isGlobalEnabled}
                  />
                </div>
              </div>
            </div>
            {/* Modal footer */}
            <div className="flex justify-end p-4">
              <button
                onClick={closeModal}
                className="bg-white border text-gray-700 hover:bg-gray-100 hover btn-sm whitespace-nowrap mr-2"
              >
                Cancel
              </button>
              <button
                className={`${!code && "opacity-40"} bg-indigo-500 hover:bg-indigo-600 btn-sm text-white whitespace-nowrap`}
                disabled={!code}
                onClick={validateTFAHandler}
              >
                Send
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </>
  );
};

export default PayoutTFAOnModal;
